import CopyButton from '@/components/blocks/CopyButton';
import { type ResolvableModalFC } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';

interface WebhookSecretRevealModalProps {
  url: string;
  secret: string;
}

export const WebhookSecretRevealModal: ResolvableModalFC<
  WebhookSecretRevealModalProps,
  void
> = ({ open, onOpenChange, resolve, url, secret }) => {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) resolve();
        onOpenChange(next);
      }}
    >
      <Dialog.Content className="max-w-lg">
        <Dialog.Header>
          <Dialog.Title>Your webhook endpoint is ready</Dialog.Title>
          <Dialog.Description>
            Copy the URL and secret now — the secret won&apos;t be shown again.
            For GitHub, or another service that signs its posts, paste the
            secret into its webhook secret field. Otherwise, send the secret in
            the X-Routine-Webhook-Secret header. If you lose it, regenerate it
            from any routine that uses this endpoint.
          </Dialog.Description>
        </Dialog.Header>
        <YStack className="gap-3 py-2">
          <YStack className="gap-1">
            <span className="text-xs font-medium text-foreground-muted">
              URL
            </span>
            <XStack className="items-center gap-1 rounded-md border border-border bg-surface px-2 py-1.5">
              <span className="min-w-0 flex-1 truncate text-sm">{url}</span>
              <CopyButton text={url} copyText="Copy webhook URL" size="sm" />
            </XStack>
          </YStack>
          <YStack className="gap-1">
            <span className="text-xs font-medium text-foreground-muted">
              Secret
            </span>
            <XStack className="items-center gap-1 rounded-md border border-border bg-surface px-2 py-1.5">
              <span className="min-w-0 flex-1 truncate text-sm">{secret}</span>
              <CopyButton
                text={secret}
                copyText="Copy webhook secret"
                size="sm"
              />
            </XStack>
          </YStack>
        </YStack>
        <Dialog.Footer className="mt-4">
          <Button
            onClick={() => {
              resolve();
              onOpenChange(false);
            }}
          >
            Done
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
